import { Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Chips from '../../components/common/Chips';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import EditCompaniesMakersModal from '../../components/companies/EditCompaniesMakersModal';
import PageHeader from '../../components/PageHeader';
import {
  useCreateCompaniesMakersMutation,
  useDeleteCompaniesMakersMutation,
  useGetCompaniesMakersDataQuery,
  useGetCompaniesMakersListQuery,
  useUpdateCompaniesMakersMutation,
} from '../../store/apiSlices/companies/companiesMakers/companiesMakersApis';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useTranslation } from 'react-i18next';

// ------------------- Types -------------------
interface CompaniesMakers {
  id?: number | string;
  srno: number;
  shortName: string;
  maker: string;
  status: string;
  actions: string;
}

interface ActionButton {
  label: string;
  variant?: 'blue' | 'outlinedBlue' | 'red' | 'gray' | 'text';
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
type MakerFormValues = {
  shortName: string;
  companyId: string;
  status: string;
};

const CompaniesMakersContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const [showAddCompaniesMakersModal, setShowAddCompaniesMakersModal] = useState<boolean>(false);
  const [editingMaker, setEditingMaker] = useState<CompaniesMakers | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MakerFormValues>({
    defaultValues: {
      shortName: '',
      companyId: '',
      status: 'Active',
    },
  });

  // API calls
  const {
    data: companiesMakersList,
    isLoading,
    refetch,
  } = useGetCompaniesMakersListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [createMaker] = useCreateCompaniesMakersMutation();
  const [updateMaker] = useUpdateCompaniesMakersMutation();
  const [deleteMaker, { isLoading: isDeleting }] = useDeleteCompaniesMakersMutation();

  const { showDeleteConfirm, handleConfirmDelete, onDeleteClick, onCloseDeleteConfirm } =
    useDeleteConfirmation<CompaniesMakers>({
      deleteMutation: deleteMaker,
      refetch,
      itemName: 'maker',
      idField: 'srno',
    });

  // Get companies makers data for the modal - only fetch when modal is open
  const { data: companiesMakersData } = useGetCompaniesMakersDataQuery(undefined, {
    skip: !showAddCompaniesMakersModal,
    refetchOnMountOrArgChange: true,
  });

  // Ensure API refetches when needed
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Page header action buttons
  const actionBtns: ActionButton[] = canAddorEdit
    ? [
        {
          label: t('companies.addMaker'),
          variant: 'blue',
          onClick: () => {
            reset({
              shortName: '',
              companyId: '',
              status: 'Active',
            });
            setEditingMaker(null);
            setShowAddCompaniesMakersModal(true);
          },
        },
      ]
    : [];

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('companies.makers'), isActive: true, onClick: () => {} },
  ];

  // Handle edit button click
  const handleEditClick = useCallback(
    (rowData: CompaniesMakers) => {
      setEditingMaker(rowData);
      reset({
        shortName: rowData.shortName,
        companyId: String(rowData.maker ?? ''),
        status: rowData.status,
      });
      setShowAddCompaniesMakersModal(true);
    },
    [reset],
  );

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'shortName',
        headerName: t('table.columns.shortName'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'maker',
        headerName: t('table.columns.company'),
        width: 250,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        width: 150,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) => <Chips width="100px" label={params?.data?.status} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
              {
                label: t('buttons.delete'),
                onClick: () => onDeleteClick(params.data),
              },
            ]}
            rowData={params.data}
          />
        ),
      },
    ],
    [handleEditClick, onDeleteClick, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Form submission handler
  const onSubmit = useCallback(
    async (values: MakerFormValues) => {
      try {
        if (editingMaker) {
          const makerId = Number((editingMaker as any)?.id ?? (editingMaker as any)?.srno);
          const payload = {
            shortName: values.shortName,
            active: values.status === 'Active',
          };
          const res: any = await updateMaker({ id: makerId, payload }).unwrap();
          showSuccessToast(res?.message);
          refetch();
          setShowAddCompaniesMakersModal(false);
          setEditingMaker(null);
          reset({
            shortName: '',
            companyId: '',
            status: 'Active',
          });
        } else {
          const payload = {
            companyId: Number(values.companyId),
            shortName: values.shortName,
            active: values.status === 'Active',
          };
          const res: any = await createMaker(payload).unwrap();
          showSuccessToast(res?.message);
          refetch();
          setShowAddCompaniesMakersModal(false);
          setEditingMaker(null);
          reset({
            shortName: '',
            companyId: '',
            status: 'Active',
          });
        }
      } catch (error) {
        logger.error('Failed to create maker', error as Error);
      }
    },
    [editingMaker, createMaker, updateMaker, refetch, reset],
  );

  // Modal close handler
  const handleCloseModal = useCallback(() => {
    setShowAddCompaniesMakersModal(false);
    setEditingMaker(null);
    reset({
      shortName: '',
      companyId: '',
      status: 'Active',
    });
  }, [reset]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={companiesMakersList ?? []}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="companies-makers-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title={t('companies.deleteMaker')}
          description={t('common.deleteConfirmation')}
          isSubmitting={isDeleting}
          onClose={onCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showAddCompaniesMakersModal && (
        <EditCompaniesMakersModal
          data={{
            companiesMakers: companiesMakersData?.companiesMakers ?? [],
            companies: companiesMakersData?.companies ?? [],
            statusOptions: companiesMakersData?.statusOptions ?? [
              { value: 'Active', label: t('common.active') },
              { value: 'Inactive', label: t('common.inactive') },
            ],
          }}
          onClose={handleCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isEditMode={!!editingMaker}
          isSubmitting={false}
        />
      )}
    </Stack>
  );
};

export default CompaniesMakersContainer;
